import { useCallback } from 'react'
import { authAPI } from '../services/api'
import { toast } from '../services/toastService'
import { trackLogin, trackSignup } from '../utils/analytics'
import { storage, sessionStorage as sessionStore } from '../utils/storage'
import { createLogger } from '../utils/logger'
import i18n from '../i18n'

const logger = createLogger('AuthOperations')

/**
 * useAuthOperations - Auth API operations hook
 * 
 * @atomic - Single responsibility: auth API operations
 * @modular - Extracted from AuthContext for better separation
 * 
 * Handles: login, register, logout, profile updates, password reset, OAuth
 */
export const useAuthOperations = (dispatch) => {
  // Login function
  const login = useCallback(async (credentials) => {
    dispatch({ type: 'AUTH_START' })
    
    try {
      const response = await authAPI.login(credentials)
      const { user } = response.data
      
      // Update i18n language from user preference if available
      if (user.language && user.language !== i18n.language) {
        i18n.changeLanguage(user.language)
      }
      
      // Tokens are now in httpOnly cookies, no need to store them
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user }
      })
      
      // Set flag to indicate user is logged in
      storage.set('auth_session_active', 'true')
      
      // Signal other tabs about login
      storage.set('auth_login_event', Date.now())
      // Clean up the signal after a moment
      setTimeout(() => storage.remove('auth_login_event'), 100)
      
      trackLogin('email')
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed'
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage })
      return { success: false, error: errorMessage }
    }
  }, [dispatch])

  // Register function
  const register = useCallback(async (userData) => {
    dispatch({ type: 'AUTH_START' })
    
    try {
      const response = await authAPI.register(userData)
      const { user } = response.data
      
      // Language already set during registration, ensure i18n matches
      if (user.language && user.language !== i18n.language) {
        i18n.changeLanguage(user.language)
      }
      
      // Tokens are now in httpOnly cookies, no need to store them
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user }
      })
      
      // Set flag to indicate user is logged in
      storage.set('auth_session_active', 'true')
      
      // Signal other tabs about login
      storage.set('auth_login_event', Date.now())
      setTimeout(() => storage.remove('auth_login_event'), 100)
      
      trackSignup('email')
      return { success: true, userId: user.id }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed'
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage })
      return { success: false, error: errorMessage }
    }
  }, [dispatch])

  // Logout function
  const logout = useCallback(async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      // Continue with logout even if API call fails
      logger.error('Logout API call failed', error)
    } finally {
      // Clear session flag
      storage.remove('auth_session_active')
      
      // httpOnly cookies will be cleared by the server
      dispatch({ type: 'LOGOUT' })
      
      // Signal other tabs to logout
      storage.set('auth_logout_event', Date.now())
      // Clean up the signal after a moment
      setTimeout(() => storage.remove('auth_logout_event'), 100)
      
      toast.success(i18n.t('toast.success.logout'))
    }
  }, [dispatch])

  // Update user profile
  const updateProfile = useCallback(async (userData, options = {}) => {
    try {
      const response = await authAPI.updateProfile(userData)
      const updatedUser = response.data
      
      dispatch({
        type: 'UPDATE_USER',
        payload: updatedUser
      })
      
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Profile update failed'
      return { success: false, error: errorMessage }
    }
  }, [dispatch])

  // Forgot password
  const forgotPassword = useCallback(async (email) => {
    try {
      await authAPI.forgotPassword(email)
      toast.success(i18n.t('toast.success.passwordResetSent'))
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send reset email'
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // Reset password
  const resetPassword = useCallback(async (data) => {
    try {
      await authAPI.resetPassword(data)
      toast.success(i18n.t('toast.success.passwordResetSuccess'))
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Password reset failed'
      toast.error(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // Google authentication
  const googleAuth = useCallback(async () => {
    try {
      // Store current page and language to return to after OAuth
      sessionStore.set('googleAuthReferrer', window.location.pathname)
      sessionStore.set('googleAuthLanguage', i18n.language || 'en')
      
      // Mark that we're going through OAuth flow
      sessionStore.set('isOAuthFlow', 'true')
      
      const response = await authAPI.googleAuth()
      const url = response.data?.url || response.url
      
      if (!url) {
        logger.error('No URL in response', { response })
        toast.error(i18n.t('toast.error.authConfigError'))
        return
      }
      
      // Use replace instead of href to avoid adding Google OAuth page to history
      // This prevents "Back" button from going back to Google account chooser
      window.location.replace(url)
    } catch (error) {
      logger.error('Google auth error', error)
      toast.error(i18n.t('toast.error.authFailed'))
    }
  }, [])

  // Set auth from stored tokens (used by OAuth callbacks)
  const setAuthFromStorage = useCallback(async (user) => {
    // Load user's language preference if available
    if (user?.language && user.language !== i18n.language) {
      i18n.changeLanguage(user.language)
    }
    
    // First dispatch the user data we received from OAuth callback
    // This ensures the UI immediately shows the user info including avatar
    dispatch({
      type: 'AUTH_SUCCESS',
      payload: { user }
    })
    
    // Set flag to indicate user is logged in
    storage.set('auth_session_active', 'true')
    
    // Add a small delay to ensure database transaction commits
    // before refreshing profile. This prevents race conditions with
    // Google OAuth avatar updates.
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Force a profile refresh to ensure we have the latest user data including avatar
    // This is especially important for OAuth flows where the avatar might have just been updated
    try {
      const response = await authAPI.getProfile()
      if (response.data && response.data.avatar !== user.avatar) {
        // Only update if avatar changed from what OAuth callback gave us
        logger.info('Profile refreshed after OAuth - avatar updated', { 
          oldAvatar: user.avatar,
          newAvatar: response.data.avatar,
          hasGoogleId: !!response.data.hasGoogleId
        })
        dispatch({
          type: 'UPDATE_USER',
          payload: response.data
        })
      }
    } catch (error) {
      // If profile fetch fails, we still have the user data from OAuth which is fine
      logger.warn('Failed to refresh profile after OAuth, using OAuth data', error)
    }
  }, [dispatch])

  // Refresh the user object from the server (useful after invite acceptance, etc.)
  const refreshProfile = useCallback(async () => {
    try {
      const response = await authAPI.getProfile()
      dispatch({ type: 'UPDATE_USER', payload: response.data })
    } catch (error) {
      logger.warn('Failed to refresh profile', error)
    }
  }, [dispatch])

  // Sync specific user fields into auth context without a network call
  const updateUserState = useCallback((fields) => {
    dispatch({ type: 'UPDATE_USER', payload: fields })
  }, [dispatch])

  return {
    login,
    register,
    logout,
    updateProfile,
    refreshProfile,
    updateUserState,
    forgotPassword,
    resetPassword,
    googleAuth,
    setAuthFromStorage
  }
}
